import { HttpClient } from '@angular/common/http';
import { isInternationalCountry } from 'core/geography';
import { environment } from 'environments/environment';
import { getSafe, isNilOrEmpty } from 'helpers';
import { of, Subject, BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilKeyChanged, map, switchMap, tap } from 'rxjs/operators';

export interface AddressAutocompleteResult {
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export class AddressAutocompleter {
  private uriBase = 'https://api.mapbox.com/geocoding/v5/mapbox.places/';
  private accessToken = environment.mapbox_access_key;
  private coordinates = [];
  private searchChanges = new Subject<{text: string; countryAbbr: string}>();

  public searching = new BehaviorSubject<boolean>(false);

  public currentResults = this.searchChanges.pipe(
    debounceTime(250),
    distinctUntilKeyChanged('text'),
    switchMap((input) => {
      if (getSafe(() => input.text.length, 0) < 5) {
        return of([]);
      } else {
        return this._searchExternal(input.text, input.countryAbbr);
      }
    })
  );

  /**
   *
   */
  constructor(private httpClient: HttpClient) {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((res) => {
        this.coordinates = [res.coords.longitude, res.coords.latitude];
      });
    }
  }

  search(text: string, countryAbbr = 'US') {
    this.searchChanges.next({text, countryAbbr});
  }

  private _searchExternal(text: string, countryAbbr: string) {
    const country = isNilOrEmpty(countryAbbr) ? 'US' : countryAbbr.toUpperCase();
    return this.httpClient.get(this.makeUri(`${text} ${country}`))
      .pipe(
        map((results: any) => {
          const transformedResults = results.features.map((feature) => ({
            streetAddress: `${feature.address || ''} ${feature.text || ''}`,
            city: getSafe(() => feature.context.find(ci => ci.id.includes('place')).text, ''),
            state: getSafe(() => feature.context.find(ci => ci.id.includes('region')).short_code.split('-')[1], ''),
            zip: getSafe(() => feature.context.find(ci => ci.id.includes('postcode')).text, ''),
            country: getSafe(() => {
              const countryCode = feature.context.find(ci => ci.id.includes('country')).short_code
              return countryCode
            }, '').toUpperCase(),
            latitude: getSafe(() => feature.geometry.coordinates[1], null),
            longitude: getSafe(() => feature.geometry.coordinates[0], null)
          } as AddressAutocompleteResult)) as AddressAutocompleteResult[];

          transformedResults.forEach((r) => {
            if (isInternationalCountry(r.country)) {
              r.state = null;
            }
          });

          return transformedResults.filter(r => r.country.includes(country));
        }),
        tap(() => this.searching.next(false))
      );
  }

  private makeUri(text: string) {
    let proximity = '';
    if (this.coordinates && this.coordinates.length > 0) {
      proximity = `&proximity=${this.coordinates.join(',')}`;
    }

    return `${this.uriBase}${encodeURIComponent(text)}.json?access_token=${this.accessToken}&limit=10&types=address${proximity}`;
  }
}
